import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { api } from '../api/client';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Message } from '../utils/chatStorage';
import { exportChatToCSV } from '../utils/csvExport';
import { getOrCreateUserId } from '../utils/userIdentity';
import { track } from '../utils/analytics';
import './ChatWindow.css';

interface ChatResponse {
response_string: string;
  response_dict: {
    questions: string[];
    urls: string[];
  };
  raise_ticket: boolean;
  is_title_set: boolean;
  title: string;
  es: number;
  module_type?: string;
  module_display_name?: string;
  classification_confidence?: number;
  attributed?: boolean;
}

interface ChatWindowProps {
  chatId: string;
  initialMessages: Message[];
  onUpdateMessages: (chatId: string, messages: Message[], title?: string) => void;
  selectedModule?: string;
  onModuleChange?: (module: string | undefined) => void;
  onNewChat?: (module?: string | undefined) => void;
  chatTitle?: string;
  escalation?: { ticketId: string; email: string };
  raiseTicketEnabled?: boolean;
  onRaiseTicketEnabled?: (chatId: string) => void;
  onRaiseTicketClick?: () => void;
}

const LOADING_MESSAGES = [
  'Thinking this through...',
  'Understanding your request...',
  'Searching through documentation...',
  'Analyzing your question...',
  'Gathering relevant information...',
  'Checking the knowledge base...',
  'Putting things together...',
  'Working on a clear answer...',
  'Refining the response...',
  'Making sure it\'s accurate...',
  'Still working on it...',
  'Thanks for your patience...',
  'Almost there...',
  'Checking available data...',
];

const sanitizeApiString = (str: string | undefined, maxLen: number): string | undefined => {
  if (!str) return undefined;
  return str.replace(/[^\x20-\x7E]/g, '').slice(0, maxLen);
};

const ChatWindow: React.FC<ChatWindowProps> = ({ chatId, initialMessages, onUpdateMessages, selectedModule, onModuleChange, onNewChat, chatTitle, escalation, raiseTicketEnabled, onRaiseTicketEnabled, onRaiseTicketClick }) => {
  const [messages, setMessages] = useState<Message[]>(
    initialMessages.length > 0
      ? initialMessages
      : [{ role: 'assistant', content: 'Hello! I\'m MIRA One, your intelligent support assistant. How can I help you today?' }]
  );
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTitleSet, setIsTitleSet] = useState(false);
  const [moduleTab, setModuleTab] = useState<string | undefined>(selectedModule);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messageTimestampsRef = useRef<number[]>([]);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [showModuleSwitchModal, setShowModuleSwitchModal] = useState(false);
  const [moduleSwitchTarget, setModuleSwitchTarget] = useState<string | undefined>(undefined);
  const isSendingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const MAX_HISTORY_TURNS = 20;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const lineHeight = 24;
    const maxHeight = 5 * lineHeight * 1.1; // ~5 lines
    const minHeight = input.trim() ? lineHeight * 2 : lineHeight; // 2 rows once typing starts
    ta.style.height = 'auto';
    ta.style.height = Math.max(minHeight, Math.min(ta.scrollHeight, maxHeight)) + 'px';
    ta.style.overflowY = ta.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [input]);

  useEffect(() => {
    if (!isLoading) return;
    setLoadingMsgIndex(0);
    const interval = setInterval(() => {
      setLoadingMsgIndex(i => (i + 1) % LOADING_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isLoading]);

  const sendMessage = async () => {
    // FIX 4 — Client-side rate limiting
    const now = Date.now();
    messageTimestampsRef.current = messageTimestampsRef.current.filter(t => now - t < 60000);
    if (messageTimestampsRef.current.length >= 10) {
      setRateLimitError('Too many messages. Please wait a moment before sending again.');
      return;
    }
    messageTimestampsRef.current.push(now);
    setRateLimitError(null);

    // FIX 7 — Combined guard including isSendingRef
    if (!input.trim() || isLoading || isSendingRef.current) return;
    isSendingRef.current = true;

    // FIX 5 — Max message length guard
    if (input.trim().length > 1500) return;

    // FIX 11 — Strip control characters from user input
    const sanitizedInput = input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').trim();
    if (!sanitizedInput) return;

    const userMessage: Message = { role: 'user', content: sanitizedInput };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.overflowY = 'hidden';
    }
    setIsLoading(true);

    // Save user message immediately
    onUpdateMessages(chatId, newMessages);

    try {
      // FIX 8 — AbortController for request cancellation
      abortControllerRef.current = new AbortController();

      // FIX 6 — Cap message history sent to API
      const historyToSend = newMessages.slice(-MAX_HISTORY_TURNS);

      // Unlike /api/share and /api/escalate (whose ownership is checked off
      // the chat document, not the session), /api/chat needs the session
      // cookie sent whenever one exists — that's the only way the backend's
      // optional_auth can attribute the turn and roll the cookie's TTL
      // forward. It never requires auth (anonymous chat stays anonymous;
      // optional_auth never raises 401), so this is safe for signed-out
      // users too — withCredentials just means "attach one if present."
      const response = await api.post<ChatResponse>(
        '/api/chat',
        {
          message_history: historyToSend,
          is_title_set: isTitleSet,
          userId: getOrCreateUserId(),
          email: '',
          chatId: chatId,
          module: moduleTab,
        },
        {
          signal: abortControllerRef.current.signal,
          // The `api` instance's 15s default is tuned for quick dashboard/auth
          // GETs — an LLM-backed chat turn (esp. Admin's multi-step agentic
          // generation) can legitimately take longer even when the backend
          // completes cleanly. Override per-call rather than raising the
          // instance default, which would also loosen timeouts that are
          // deliberately short elsewhere.
          timeout: 30000,
        }
      );

      // FIX 10 — Sanitize title and module_type from API response
      const safeTitle = sanitizeApiString(response.data.title, 120);
      const safeModule = sanitizeApiString(response.data.module_type, 40);

      const assistantMessage: Message = {
        role: 'assistant',
        content: response.data.response_string,
        moduleDisplayName: response.data.module_display_name
      };

      const updatedMessages = [...newMessages, assistantMessage];
      setMessages(updatedMessages);

      // Switch tab to detected module if in Ask Anything mode
      const resolvedModule = safeModule ?? moduleTab;
      if (moduleTab === undefined && safeModule) {
        setModuleTab(safeModule);
        if (onModuleChange) {
          onModuleChange(safeModule);
        }
      }

      const userTurnCount = newMessages.filter(m => m.role === 'user').length;
      track('message_sent', {
        module: resolvedModule ?? 'unknown',
        source: moduleTab !== undefined ? 'user' : 'auto',
        turn: userTurnCount,
      });

      // Save with assistant message and title if generated
      if (response.data.is_title_set && safeTitle && !isTitleSet) {
        setIsTitleSet(true);
        onUpdateMessages(chatId, updatedMessages, safeTitle);
      } else {
        onUpdateMessages(chatId, updatedMessages);
      }

      // Persist raise_ticket flag — once true, always true for this chat
      if (response.data.raise_ticket && !raiseTicketEnabled && onRaiseTicketEnabled) {
        onRaiseTicketEnabled(chatId);
      }

      // FIX 9 — Log classification info only in development
      if (process.env.NODE_ENV === 'development') {
        if (safeModule) {
          console.log(
            `Classified as: ${safeModule} ` +
            `(confidence: ${response.data.classification_confidence?.toFixed(2)})`
          );
        }
      }
    } catch (error) {
      // FIX 8 — Handle cancellations gracefully
      if (axios.isCancel(error)) {
        // Request was aborted — do not add an error message to the chat
      } else {
        // FIX 9 — Only log errors in development
        if (process.env.NODE_ENV === 'development') { console.error('Error sending message:', error); }
        const errorMessages = [
          ...newMessages,
          {
            role: 'assistant',
            content: 'I apologize, but I encountered an error. Please try again or contact support.'
          }
        ];
        setMessages(errorMessages);
        onUpdateMessages(chatId, errorMessages);
      }
    } finally {
      setIsLoading(false);
      // FIX 7 — Release send guard
      isSendingRef.current = false;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleModuleChange = (module: string | undefined) => {
    const hasMessages = messages.length > 1;
    const isDifferentModule = module !== moduleTab;

    if (hasMessages && isDifferentModule) {
      setModuleSwitchTarget(module);
      setShowModuleSwitchModal(true);
    } else {
      setModuleTab(module);
      track('module_selected', { module: module ?? 'ask_anything' });
      if (onModuleChange) {
        onModuleChange(module);
      }
    }
  };

  const confirmModuleSwitch = () => {
    track('module_selected', { module: moduleSwitchTarget ?? 'ask_anything' });
    if (onNewChat) {
      onNewChat(moduleSwitchTarget);
    }
    setModuleSwitchTarget(undefined);
    setShowModuleSwitchModal(false);
  };

  const cancelModuleSwitch = () => {
    setModuleSwitchTarget(undefined);
    setShowModuleSwitchModal(false);
  };

  const handleDownloadChat = () => {
    const title = chatTitle || 'Chat';
    exportChatToCSV(messages, title);
  };

  const isEmpty = messages.length === 1 && messages[0].role === 'assistant' && !isLoading;

  const MODULE_PLACEHOLDERS: Record<string, string> = {
    resident: 'Ask about the Resident App...',
    admin: 'Ask about the Admin Dashboard...',
    smart_devices: 'Ask about Smart Devices...',
  };
  const inputPlaceholder = moduleTab ? (MODULE_PLACEHOLDERS[moduleTab] ?? 'Ask anything...') : 'Ask anything...';

  const inputBar = (centered: boolean) => (
    <div className={`input-container${centered ? ' input-container-centered' : ''}`}>
      <div className="input-wrapper">
        <button
          className="download-chat-button-input"
          onClick={handleDownloadChat}
          title="Download chat as CSV"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
        </button>
        <textarea
          id="chat-message-input"
          name="message"
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask anything..."
          disabled={isLoading}
          rows={1}
          maxLength={1500}
          style={{ overflowY: 'hidden' }}
        />
        <button className="send-message-button" onClick={() => sendMessage()} disabled={isLoading || !input.trim()}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>
      <div style={{ textAlign: 'right', fontSize: '0.6875rem', color: input.length > 1350 ? '#dc2626' : '#9ca3af', padding: '2px 16px 0' }}>
        {input.length}/1500
      </div>
      <div className="input-footer-disclaimer">
        MIRA is an AI Chatbot and can make mistakes. Please verify important information.
      </div>
    </div>
  );

  return (
    <div className="chat-window">
      <div className="module-tabs">
        <button
          className={`module-tab ${moduleTab === undefined ? 'active' : ''}`}
          onClick={() => handleModuleChange(undefined)}
        >
          Ask Anything
        </button>
        <button
          className={`module-tab module-tab-resident ${moduleTab === 'resident' ? 'active' : ''}`}
          onClick={() => handleModuleChange('resident')}
        >
          Resident App
        </button>
        <button
          className={`module-tab module-tab-admin ${moduleTab === 'admin' ? 'active' : ''}`}
          onClick={() => handleModuleChange('admin')}
        >
          Admin Dashboard
        </button>
        <button
          className={`module-tab module-tab-smart ${moduleTab === 'smart_devices' ? 'active' : ''}`}
          onClick={() => handleModuleChange('smart_devices')}
        >
          Smart Devices
        </button>
      </div>

      {isEmpty ? (
        <div className="empty-state">
          <div className="empty-state-heading">
            <span>How can I help you today?</span>
          </div>
          {rateLimitError && (
            <div style={{ textAlign: 'center', padding: '8px', fontSize: '0.8125rem', color: '#dc2626' }}>
              {rateLimitError}
            </div>
          )}
          {inputBar(true)}
        </div>
      ) : (
        <>
          <div className="messages-container">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}>
                <div className="message-header">
                  <span className="message-role">{msg.role === 'user' ? 'You' : 'MIRA'}</span>
                  {msg.role === 'assistant' && msg.moduleDisplayName && (
                    <span className={`module-tag module-tag-${msg.moduleDisplayName.toLowerCase().replace(/\s+/g, '-')}`}>
                      {msg.moduleDisplayName}
                    </span>
                  )}
                </div>
                <div className="message-content">
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkBreaks, remarkGfm]}
                      rehypePlugins={[rehypeSanitize]}
                      components={{
                        img: ({ src, alt }) => (
                          <img
                            src={src}
                            alt={alt}
                            onLoad={(e) => {
                              const el = e.currentTarget;
                              if (el.naturalHeight > el.naturalWidth) {
                                el.classList.add('portrait-image');
                              }
                            }}
                          />
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message assistant-message">
                <div className="message-header">
                  <span className="message-role">MIRA</span>
                </div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="loading-status-text">{LOADING_MESSAGES[loadingMsgIndex]}</div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {rateLimitError && (
            <div style={{ textAlign: 'center', padding: '8px', fontSize: '0.8125rem', color: '#dc2626' }}>
              {rateLimitError}
            </div>
          )}
          {escalation ? (
            <div className="escalation-banner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: '#059669' }}>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <div>
                <div className="escalation-banner-title">This conversation has moved to email</div>
                <div className="escalation-banner-body">
                  Please check for ticket ID <strong>{escalation.ticketId}</strong> in the subject line in <strong>{escalation.email}</strong>'s inbox. Make sure you check your <strong>SPAM folder</strong>.
                </div>
              </div>
            </div>
          ) : raiseTicketEnabled ? (
            <div className="raise-ticket-cta">
              <p className="raise-ticket-cta-text">
                It looks like you may need further assistance. Would you like to raise a support ticket?
              </p>
              <button className="raise-ticket-cta-button" onClick={onRaiseTicketClick}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Raise Ticket
              </button>
            </div>
          ) : (
            inputBar(false)
          )}
        </>
      )}

      {showModuleSwitchModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '28px 32px',
            maxWidth: '380px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '0.9375rem', fontWeight: 700, color: '#111827' }}>
              Switch topic?
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '0.844rem', color: '#6b7280', lineHeight: 1.6 }}>
              Switching topics will start a new chat. Your current conversation will be saved in the sidebar.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={cancelModuleSwitch} style={{
                padding: '8px 18px', borderRadius: '7px', border: '1px solid #e5e7eb',
                background: '#f9fafb', color: '#374151', fontSize: '0.8125rem', cursor: 'pointer', fontWeight: 500
              }}>Cancel</button>
              <button onClick={confirmModuleSwitch} style={{
                padding: '8px 18px', borderRadius: '7px', border: 'none',
                background: 'var(--mg-yellow)', color: '#111827', fontSize: '0.8125rem', cursor: 'pointer', fontWeight: 600
              }}>Start new chat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
